import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import type { AdminDeveloperKey } from "@ternary/contracts"
import {
  Badge,
  Button,
  Card,
  type Column,
  ConfirmDialog,
  DescriptionList,
  Field,
  IconButton,
  Input,
  Table,
} from "@ternary/ui"
import { useState } from "react"
import { Link } from "react-router-dom"
import {
  AdminApiError,
  createMcpToken,
  loadAdminDeveloperSettings,
  loadMcpConnections,
  loadMcpTeam,
  loadMcpTokens,
  revokeMcpConnection,
  revokeMcpTeammateAccess,
  revokeMcpToken,
  rotateAdminDeveloperKey,
} from "../api.js"
import { FormLoading } from "../components/form-loading.js"
import { errorDetail, LoadError } from "../components/load-error.js"
import { McpSettings, McpTeamCard } from "../components/mcp-settings.js"
import { NavIcon } from "../components/nav-icons.js"
import { SettingsHeader } from "./admin-settings-page.js"

function createdDate(value: string) {
  return new Intl.DateTimeFormat("en-US", { dateStyle: "medium" }).format(new Date(value))
}

/** Each endpoint: its methods, its path and what it is for. */
const ENDPOINTS = [
  [["GET"], "/v1/products", "Products and their Discord access rules"],
  [["GET"], "/v1/users", "Member licenses and metadata"],
  [["GET", "PATCH"], "/v1/users/:key", "Look up a license and update its metadata"],
] as const

/**
 * Claude (MCP), while the deployment offers it: the person's tokens and connected apps, loaded
 * here, created and revoked through McpSettings. A new token shows once.
 */
export function ClaudeMcpSection() {
  const queryClient = useQueryClient()
  const tokens = useQuery({
    queryFn: ({ signal }) => loadMcpTokens({ signal }),
    queryKey: ["admin-mcp-tokens"],
  })
  const connections = useQuery({
    queryFn: ({ signal }) => loadMcpConnections({ signal }),
    queryKey: ["admin-mcp-connections"],
  })
  // Owners only: anyone else is refused (403), and sees no team card.
  const team = useQuery({
    queryFn: ({ signal }) => loadMcpTeam({ signal }),
    queryKey: ["admin-mcp-team"],
    retry: false,
  })
  const [issued, setIssued] = useState<{ id: string; token: string } | null>(null)
  // The last create or revoke's failure only: a later one that works takes it off the page.
  const [failure, setFailure] = useState<unknown>(null)
  const tracked = {
    onError: (error: unknown) => setFailure(error),
    onMutate: () => setFailure(null),
  }
  const refresh = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: ["admin-mcp-tokens"] }),
      queryClient.invalidateQueries({ queryKey: ["admin-mcp-connections"] }),
      queryClient.invalidateQueries({ queryKey: ["admin-mcp-team"] }),
    ])
  const endTeammate = useMutation({
    ...tracked,
    mutationFn: revokeMcpTeammateAccess,
    onSettled: refresh,
  })
  // The token shown stays until a new one replaces it: a create that fails leaves the secret of a
  // token that still works on the page.
  const create = useMutation({
    ...tracked,
    mutationFn: createMcpToken,
    async onSuccess(created) {
      setIssued({ id: created.id, token: created.token })
      await refresh()
    },
  })
  const revoke = useMutation({
    ...tracked,
    mutationFn: ({ id, kind }: { id: string; kind: "token" | "connection" }) =>
      kind === "token" ? revokeMcpToken(id) : revokeMcpConnection(id),
    onSettled: refresh,
    // A revoked token's secret is dead: it never stays under "Copy this token now". Revoking
    // another one (the old token, while rotating) keeps the new secret.
    onSuccess: (_answer, { id, kind }) => {
      if (kind === "token") setIssued((shown) => (shown?.id === id ? null : shown))
    },
  })

  if (tokens.isPending || connections.isPending) {
    return <FormLoading fields={2} label="Loading Claude (MCP)…" />
  }
  const loadError =
    tokens.isError || connections.isError ? (
      <LoadError
        detail={errorDetail(tokens.error ?? connections.error)}
        message="We couldn't load Claude (MCP)"
        onRetry={() => void refresh()}
        retrying={tokens.isFetching || connections.isFetching}
      />
    ) : null
  // A reload that fails after a first read keeps the page, so a token's one-time secret stays.
  if (!tokens.data || !connections.data) return loadError
  return (
    <>
      {loadError}
      {failure instanceof Error ? (
        <p className="tny-error" role="alert">
          {failure.message}
        </p>
      ) : null}
      <McpSettings
        canConnect={tokens.data.canConnect}
        connections={connections.data.connections}
        creating={create.isPending}
        issuedToken={issued?.token ?? null}
        onCreateToken={(label) => create.mutate(label)}
        onRevokeConnection={(id) => revoke.mutate({ id, kind: "connection" })}
        onRevokeToken={(id) => revoke.mutate({ id, kind: "token" })}
        revoking={revoke.isPending}
        tokens={tokens.data.tokens}
        url={tokens.data.url}
      />
      {team.data ? (
        <McpTeamCard
          onRevoke={(access) => endTeammate.mutate(access)}
          people={team.data.people}
          revoking={endTeammate.isPending}
        />
      ) : team.isError && !(team.error instanceof AdminApiError && team.error.status === 403) ? (
        <LoadError
          detail={errorDetail(team.error)}
          message="We couldn't load your team's Claude access"
          onRetry={() => void team.refetch()}
          retrying={team.isFetching}
        />
      ) : null}
    </>
  )
}

/**
 * The API key: generate the first one, or rotate it after a ConfirmDialog, because the current key
 * stops working at once. The new secret shows once, with Copy.
 */
export function AdminDeveloperPage() {
  const queryClient = useQueryClient()
  const query = useQuery({
    queryFn: loadAdminDeveloperSettings,
    queryKey: ["admin-developer-settings"],
  })
  const [issuedSecret, setIssuedSecret] = useState("")
  const [confirming, setConfirming] = useState(false)
  const [notice, setNotice] = useState("")
  const [actionError, setActionError] = useState("")
  const rotate = useMutation({
    mutationFn: rotateAdminDeveloperKey,
    onMutate() {
      setIssuedSecret("")
      setActionError("")
      setNotice("")
    },
    onSuccess(data) {
      queryClient.setQueryData(["admin-developer-settings"], {
        key: data.key,
        mode: "live",
        writable: query.data?.writable ?? true,
      })
      setIssuedSecret(data.secretKey)
      setNotice("API key rotated. Copy the new key now.")
    },
    onError(error) {
      setActionError(error instanceof Error ? error.message : "API key could not be rotated.")
    },
    onSettled() {
      setConfirming(false)
    },
  })

  if (query.isPending || query.isError) {
    return (
      <section aria-busy={query.isPending || undefined} className="admin-stack admin-page">
        <SettingsHeader active="developer" />
        {query.isPending ? (
          <FormLoading fields={2} label="Loading developer settings…" />
        ) : (
          <LoadError
            detail={errorDetail(query.error)}
            message="We couldn't load your developer settings"
            onRetry={() => void query.refetch()}
            retrying={query.isFetching}
          />
        )}
      </section>
    )
  }
  const developer = query.data

  async function copyMaskedKey(maskedKey: string) {
    try {
      await navigator.clipboard.writeText(maskedKey)
      setNotice("Masked key copied.")
    } catch {
      setNotice("Select the key and copy it.")
    }
  }

  async function copySecret() {
    try {
      await navigator.clipboard.writeText(issuedSecret)
      setNotice("API key copied.")
    } catch {
      setNotice("Select the key and copy it before leaving this page.")
    }
  }

  const columns: Column<AdminDeveloperKey>[] = [
    {
      // One line, cut short where it has no room, never broken mid-key; its title holds it whole.
      cell: (key) => (
        <span className="admin-url">
          <code className="admin-mono admin-url-text" title={key.maskedKey}>
            {key.maskedKey}
          </code>
          <IconButton
            icon={<NavIcon name="copy" />}
            label="Copy masked key"
            onClick={() => void copyMaskedKey(key.maskedKey)}
            size="sm"
          />
        </span>
      ),
      header: "Key",
      key: "key",
    },
    {
      cell: (key) => createdDate(key.createdAt),
      header: "Created",
      key: "created",
      priority: 2,
    },
    {
      align: "end",
      cell: () => (
        <Button
          disabled={!developer.writable}
          onClick={() => setConfirming(true)}
          size="sm"
          variant="secondary"
        >
          Rotate API key
        </Button>
      ),
      header: "Actions",
      key: "actions",
    },
  ]

  return (
    <section className="admin-stack admin-page">
      <SettingsHeader active="developer" />
      {actionError ? (
        <p className="tny-error" role="alert">
          {actionError}
        </p>
      ) : null}
      <p className="admin-note" role="status">
        {notice}
      </p>
      {issuedSecret ? (
        <Card title="Copy this key now">
          <div className="admin-stack">
            <p className="admin-note">For security, it will not be shown again.</p>
            <div className="admin-field-action">
              <Field label="New secret API key">
                <Input className="admin-mono" readOnly value={issuedSecret} />
              </Field>
              <Button onClick={() => void copySecret()} variant="secondary">
                Copy key
              </Button>
            </div>
          </div>
        </Card>
      ) : null}

      <Card
        actions={
          developer.key ? null : (
            <Button
              disabled={!developer.writable}
              loading={rotate.isPending}
              onClick={() => rotate.mutate()}
            >
              Generate API key
            </Button>
          )
        }
        title="API keys"
      >
        <div className="admin-stack">
          {developer.key ? (
            <Table
              caption="API key"
              captionHidden
              columns={columns}
              // One row, kept mounted through a rotation, so focus returns to Rotate API key.
              rowKey={() => "api-key"}
              rows={[developer.key]}
            />
          ) : (
            <p className="admin-note">
              Generate a key to authenticate requests to the Ternary v1 API.
            </p>
          )}
          {developer.key?.migrationRequired ? (
            <p className="admin-note">
              This key uses an older format. It upgrades automatically the next time it's used.
            </p>
          ) : null}
          <p className="admin-note admin-inline-links">
            Send the key as <code className="admin-mono">Authorization: Bearer YOUR_KEY</code>. Read
            the{" "}
            <a
              href="https://docs.ternarydev.com/ternary-developments"
              rel="noreferrer"
              target="_blank"
            >
              API documentation
            </a>
            .
          </p>
        </div>
      </Card>

      {developer.mcp ? <ClaudeMcpSection /> : null}

      <Card title="Available endpoints">
        {/* The paths share a column as wide as the longest, so none wraps (app.css). */}
        <div className="admin-endpoints">
          <DescriptionList
            items={ENDPOINTS.map(([methods, path, purpose]) => ({
              term: (
                <span className="admin-endpoint">
                  {methods.map((method) => (
                    <Badge key={method}>{method}</Badge>
                  ))}
                  <code className="admin-mono admin-endpoint-path">{path}</code>
                </span>
              ),
              value: purpose,
            }))}
          />
        </div>
      </Card>

      <Card title="External scripts">
        <div className="admin-stack">
          <p className="admin-note">
            Analytics and ad tracking are set up as pixel IDs, so no outside script runs on your
            pages.
          </p>
          <div>
            <Link
              className="tny-button tny-button-secondary admin-button-link"
              to="/dashboard/pixels"
            >
              Open Tracking Pixels
            </Link>
          </div>
        </div>
      </Card>

      <ConfirmDialog
        body="The current key stops working at once. Anything that uses it needs the new key."
        confirmLabel="Rotate key"
        danger
        onCancel={() => setConfirming(false)}
        onConfirm={() => rotate.mutate()}
        open={confirming}
        pending={rotate.isPending}
        title="Rotate the API key?"
      />
    </section>
  )
}
